import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
	PageHeader,
	Button,
	Typography,
	Modal,
	Card,
	message,
	Input,
	Divider,
	Tooltip,
	Form,
} from 'antd';

import Meta from '../../../../../components/Meta';
import Fallback from '../../../../../components/Fallback';
import BlockTemplatesAPI from '../../../../../services/sdks/blockTemplates';
import { useScript } from '../../../../../hooks';
import { Container, ButtonContainer, Grid, OptionsContainer, ScriptWrapper } from './styles';
import ElementsTabs from '../../../../../components/ElementsTabs';
import { FiMaximize, FiMinimize, FiSave, FiXSquare } from 'react-icons/fi';
import Script from '../../../../../components/Script';
import { connect } from 'react-redux';
import TKVoiceConfigAPI from '../../../../../services/sdks/tkVoiceConfig';
import ElementsAPI from '../../../../../services/sdks/elements';

const breadcrumb = {
	routes: [
		{ breadcrumbName: 'Painel administrativo' },
		{ breadcrumbName: 'Templates' },
		{ breadcrumbName: 'Templates de Bloco' },
		{ breadcrumbName: 'Editar Template' },
	],
	style: { marginBottom: 12 },
};

const UpdateScriptTemplate = ({ user }) => {
	const { templateId } = useParams();
	const [fallback, setFallback] = useState({ initialData: true });
	const [errors, setErrors] = useState({});
	const [name, setName] = useState('');
	const [tkVoiceTypes, setTkVoiceTypes] = useState({ caster: [], local: [] });
	const history = useHistory();
	const {
		cleanUp,
		script,
		setScript,
		isSorting,
		setIsSorting,
		onElementMove,
		compact,
		setCompact,
		setElements,
	} = useScript();

	const handleUpdateTemplate = useCallback(async () => {
		setErrors({});

		if (!name) {
			setErrors((prev) => ({ ...prev, name: true }));
			return message.error('Informe o nome do template');
		}

		if (!script.body?.length) {
			return message.error('Preencha o modelo');
		}

		setFallback((prev) => ({ ...prev, updatingTemplate: true }));

		try {
			await BlockTemplatesAPI.update({ payload: { name, body: script.body }, id: templateId });
			message.success('Template atualizado com sucesso');
			setFallback((prev) => ({ ...prev, updatingTemplate: false }));
			history.push(`/admin/templates/block-templates`);
		} catch (error) {
			setFallback((prev) => ({ ...prev, updatingTemplate: false }));
			console.error(error);
			message.error('Houve um erro, tente novamente');
			message.error(error?.response?.data?.message);
		}
	}, [name, script.body, templateId, history]);

	useEffect(() => {
		cleanUp();
		return () => cleanUp();
	}, [cleanUp]);

	useEffect(() => {
		const fetchTemplate = async () => {
			try {
				const { data } = await BlockTemplatesAPI.getOne({ id: templateId });
				setName(data.template.name);
				setScript(data.template);
			} catch (error) {
				message.error('Houve um erro ao buscar o template');
			}

			setFallback((prev) => ({ ...prev, initialData: false }));
		};

		fetchTemplate();
	}, [setScript, templateId]);

	useEffect(() => {
		const fetchTkVoiceTypes = async () => {
			try {
				const res = await TKVoiceConfigAPI.getPopulatedTypes('', '');
				setTkVoiceTypes(res.data.types);
			} catch (error) {
				setTkVoiceTypes({ caster: [], local: [] });
			}
		};

		const fetchTemplateVignetes = async () => {
			try {
				const res = await ElementsAPI.custom.index({ query: 'type=VIGNETTE-TEMPLATE' });
				setElements((prev) => ({ ...prev, templateVignettes: res.data.elements }));
			} catch (error) {
				message.error('Erro ao buscar as vinhetas de template');
				setElements((prev) => ({ ...prev, templateVignettes: [] }));
			}
		};

		fetchTemplateVignetes();
		fetchTkVoiceTypes();
	}, [setElements]);

	if (fallback?.initialData) {
		return <Fallback title='Carregando...' message='Por favor aguarde...' />;
	}

	return (
		<>
			<Meta title='Editar Template de Bloco' />

			<PageHeader title='Editar Template de Bloco' breadcrumb={breadcrumb}></PageHeader>

			<Container isSorting={isSorting}>
				<Card>
					<Grid>
						<ElementsTabs
							isAdmin={true}
							tkVoiceTypes={tkVoiceTypes}
							isBlockTemplate={true}
							isTemplate={true}
							recplaySegmentTemplates={[]}
						/>

						<ScriptWrapper>
							<Typography.Title level={4}>Template</Typography.Title>

							<Divider style={{ margin: '8px 0' }} />

							<OptionsContainer>
								<div className='divider'></div>

								<Tooltip title={compact ? 'Maximizar' : 'Compactar'}>
									<Button
										size='small'
										onClick={() => setCompact(!compact)}
										disabled={script?.body?.length === 0}>
										{compact ? <FiMaximize /> : <FiMinimize />}
									</Button>
								</Tooltip>

								<Tooltip
									title={script?.body?.length !== 0 && !script?.isShared && 'Limpar template'}>
									<Button
										disabled={script?.body?.length === 0 || script?.isShared}
										type='danger'
										size='small'
										onClick={() => {
											Modal.confirm({
												title: 'Limpar template?',
												icon: 'exclamation-circle',
												content: 'Todos os blocos e elementos serão removidos, deseja continuar?',
												okText: 'Limpar template',
												onOk: () => setScript({ ...script, body: [] }),
												okButtonProps: {
													icon: 'delete',
													type: 'danger',
												},
												cancelText: 'Cancelar',
												cancelButtonProps: {
													icon: 'close-circle',
												},
											});
										}}>
										<FiXSquare />
									</Button>
								</Tooltip>
							</OptionsContainer>

							<Divider style={{ margin: '8px 0' }} />

							<Script
								onSortEnd={onElementMove}
								onSortStart={() => setIsSorting(true)}
								loggedUser={user}
								tkVoiceTypes={tkVoiceTypes}
								isAdmin={true}
								recplaySegmentTemplates={[]}
							/>
						</ScriptWrapper>
					</Grid>

					<Form.Item
						style={{ marginTop: 16 }}
						validateStatus={errors['name'] && 'error'}
						help={errors['name'] && 'Informe o nome do template'}>
						<Input
							value={name}
							onChange={(e) => setName(e.target.value)}
							placeholder='Nome do template'
							size='large'
						/>
					</Form.Item>

					<ButtonContainer>
						<Button
							size='large'
							type='primary'
							disabled={!script.body?.length}
							onClick={handleUpdateTemplate}
							loading={fallback?.updatingTemplate}>
							<FiSave /> Salvar Template
						</Button>
					</ButtonContainer>
				</Card>
			</Container>
		</>
	);
};

export default connect(({ user }) => ({ user }))(UpdateScriptTemplate);
